import { useEffect, useState } from "react";
import { ImageOff, Package } from "lucide-react";

import { fetchMediaObjectUrl } from "@/api/client";
import { cn } from "@/lib/cn";

/**
 * A stored photo.
 *
 * Media sits behind authentication (MinIO is not reachable from outside), and an
 * `<img src>` cannot carry a bearer token — so the bytes are fetched with the
 * header and handed to the browser as an object URL, revoked on unmount.
 *
 * Photos are here to make an item *recognisable*, not to decorate: a missing one
 * falls back to a neutral icon rather than a broken frame.
 */
export function Photo({
  mediaId,
  alt,
  className,
  rounded = "rounded-md",
}: {
  mediaId: string | null | undefined;
  alt: string;
  className?: string;
  rounded?: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!mediaId) {
      setUrl(null);
      return;
    }
    const controller = new AbortController();
    let objectUrl: string | null = null;
    setFailed(false);

    fetchMediaObjectUrl(mediaId, controller.signal)
      .then((created) => {
        objectUrl = created;
        setUrl(created);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId]);

  const frame = cn(
    "flex items-center justify-center overflow-hidden border border-border bg-surface-sunken",
    rounded,
    className,
  );

  if (!mediaId) {
    return (
      <div className={frame} aria-label={`${alt} — aucune photo`} role="img">
        <Package className="size-1/2 text-ink-subtle" aria-hidden />
      </div>
    );
  }

  if (failed) {
    return (
      <div className={frame} aria-label={`${alt} — photo indisponible`} role="img">
        <ImageOff className="size-1/2 text-ink-subtle" aria-hidden />
      </div>
    );
  }

  if (!url) return <div className={cn(frame, "skeleton")} aria-hidden />;

  return (
    <div className={frame}>
      <img src={url} alt={alt} className="size-full object-cover" loading="lazy" />
    </div>
  );
}
